import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cog6ToothIcon, KeyIcon, ClockIcon, TrashIcon, CreditCardIcon } from '@heroicons/react/24/outline';

// Layout
import StandardLayout from '../../components/layout/StandardLayout';

// Services
import { deleteUserQuestionHistory } from '../../services/statAPIService';
import { changePassword, deleteUser, getUserEmail } from '../../services/userAPIService';
import { getSubscriptionStatus } from '../../services/paymentService';

// Context
import { AuthContext } from '../../context/AuthContext';

const SettingsPage = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [status, email] = await Promise.all([
          getSubscriptionStatus(),
          getUserEmail()
        ]);
        setSubscriptionStatus(status);
        setUserEmail(email);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    try {
      await changePassword({ current_password: oldPassword, new_password: newPassword });
      alert('Password changed successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      alert('Error changing password: ' + error.detail);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Bist du sicher, dass du dein Konto löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      try {
        await deleteUser();
        await logout();
        navigate('/');
      } catch (error) {
        alert('Error deleting account: ' + (error.detail || 'Unknown error'));
      }
    }
  };

  const handleDeleteUserQuestionHistory = () => {
    if (window.confirm('Bist du sicher, dass du deinen Lernverlauf löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      deleteUserQuestionHistory();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <StandardLayout>
      <div className="min-h-screen bg-base-100">
        {/* Hero Section */}
        <div>
          <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="text-center">
              <div className="inline-flex p-4 rounded-full bg-primary/10 ring-1 ring-primary/20 mb-6">
                <Cog6ToothIcon className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-base-content">
                Einstellungen
              </h1>
              <p className="text-base-content/70 max-w-2xl mx-auto">
                Verwalte dein Konto und passe deine Einstellungen an
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <div className="space-y-6">
            {/* Subscription Status */}
            <div className="card bg-base-200 shadow-lg">
              <div className="card-body">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-success/10">
                    <CreditCardIcon className="w-6 h-6 text-success" />
                  </div>
                  <h2 className="card-title text-xl">Abo Status</h2>
                </div>
                
                {subscriptionStatus && (
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2">
                      <p className="text-base-content">
                        Status: {subscriptionStatus.has_subscription ? (
                          <span className="text-success font-medium">Aktiv</span>
                        ) : (
                          <span className="text-error font-medium">Inaktiv</span>
                        )}
                      </p>
                      {subscriptionStatus.subscription_end_date && (
                        <p className="text-base-content">
                          Gültig bis: {formatDate(subscriptionStatus.subscription_end_date)}
                        </p>
                      )}
                    </div>

                    {subscriptionStatus.has_subscription && (
                      <div className="space-y-4">
                        {/* Zahlungsbelege */}
                        <div className="flex flex-col gap-2">
                          <p className="text-base-content font-medium">Zahlungsbelege</p>
                          <a 
                            href={`mailto:julian.m.bloechl@gmail.com?subject=Rechnung%20für%20Mein-SKS%20Zugang&body=Sehr%20geehrtes%20Mein-SKS%20Team%2C%0A%0Aich%20möchte%20gerne%20eine%20Rechnung%20für%20meinen%20Mein-SKS%20Zugang%20anfordern.%0A%0AMeine%20E-Mail-Adresse%3A%20${encodeURIComponent(userEmail)}%0A%0AMit%20freundlichen%20Grüßen`}
                            className="btn btn-outline btn-sm"
                          >
                            Rechnung anfordern
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Passwortverwaltung */}
            <div className="card bg-base-200 shadow-lg">
              <div className="card-body">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <KeyIcon className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="card-title text-xl">Passwort ändern</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Aktuelles Passwort</span>
                    </label>
                    <input 
                      type="password"
                      className="input input-bordered bg-base-100"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Neues Passwort</span>
                    </label>
                    <input 
                      type="password"
                      className="input input-bordered bg-base-100"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Neues Passwort bestätigen</span>
                    </label>
                    <input 
                      type="password"
                      className="input input-bordered bg-base-100"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <button 
                    className="btn btn-primary w-full"
                    onClick={handlePasswordReset}
                  >
                    Passwort aktualisieren
                  </button>
                </div>
              </div>
            </div>

            {/* Verlaufsverwaltung */}
            <div className="card bg-base-200 shadow-lg">
              <div className="card-body">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-warning/10">
                    <ClockIcon className="w-6 h-6 text-warning" />
                  </div>
                  <h2 className="card-title text-xl">Lernverlauf</h2>
                </div>
                
                <p className="text-base-content/70 mb-4">
                  Hier kannst du deinen gesamten Lernfortschritt zurücksetzen. Diese Aktion kann nicht rückgängig gemacht werden.
                </p>

                <button 
                  className="btn btn-warning btn-outline w-full"
                  onClick={handleDeleteUserQuestionHistory}
                >
                  Lernverlauf zurücksetzen
                </button>
              </div>
            </div>

            {/* Kontoverwaltung */}
            <div className="card bg-base-200 shadow-lg">
              <div className="card-body">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-error/10">
                    <TrashIcon className="w-6 h-6 text-error" />
                  </div>
                  <h2 className="card-title text-xl">Konto löschen</h2>
                </div>
                
                <div className="alert alert-error bg-error/10 border border-error/20 text-base-content">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Achtung: Das Löschen deines Kontos kann nicht rückgängig gemacht werden.</span>
                </div>

                <button 
                  className="btn btn-error w-full mt-4"
                  onClick={handleDeleteAccount}
                >
                  Konto endgültig löschen - Das bezahlte Abo wird nicht erstattet
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StandardLayout>
  );
};

export default SettingsPage;
